import React, { useMemo, useState, useEffect } from 'react';
import { ArrowLeftRight, ChevronDown, TrendingUp, TrendingDown, Sliders } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { formatDate, formatLocalTime } from '../../lib/dateUtils';
import { EGG_LABELS, MOVIMIENTO_COLORS, MOVIMIENTO_LABELS, normalizeTipoKey } from './constants';

// Sets de clasificación de movimientos (idéntico al backend)
const SET_ENTRADAS = new Set(['Entrada', 'Cotejo inicial', 'Ingreso', 'Traslado Entrada', 'Reverso']);
const SET_SALIDAS = new Set(['Salida', 'Rotura', 'Egreso', 'Traslado Salida']);
const SET_AJUSTES = new Set(['Ajuste']);

// Extrae 'YYYY-MM-DD' del campo fecha (que puede venir con T00:00:00).
const dayKey = (f) => (f || '').slice(0, 10);

/**
 * Historial de movimientos de inventario agrupado por día.
 *
 * Layout: Card > Filtros arriba > Bloques colapsables por fecha (accordion).
 * - Cada día muestra: fecha + KPIs (entradas / salidas / ajustes) + subtotales por tipo de huevo.
 * - Al expandir: tabla con el detalle de movimientos del día.
 * - Por defecto se cargan los ÚLTIMOS 7 DÍAS. Usá el filtro de rango para ver otros.
 * - El día más reciente se abre solo.
 */
export const MovimientosTab = ({
  movimientos,
  filtroMovimiento, setFiltroMovimiento,
  filtroTipo, setFiltroTipo,
  filtroFechaDesde, setFiltroFechaDesde,
  filtroFechaHasta, setFiltroFechaHasta,
}) => {
  const hayFiltros =
    (filtroMovimiento && filtroMovimiento !== 'all') ||
    (filtroTipo && filtroTipo !== 'all') ||
    filtroFechaDesde ||
    filtroFechaHasta;

  const limpiarTodo = () => {
    setFiltroTipo('all');
    setFiltroMovimiento('all');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
  };

  // Agrupación por fecha
  const gruposPorFecha = useMemo(() => {
    if (!movimientos?.length) return [];
    const bucket = new Map();
    for (const mov of movimientos) {
      const k = dayKey(mov.fecha);
      if (!k) continue;
      if (!bucket.has(k)) {
        bucket.set(k, {
          fecha: k,
          movimientos: [],
          totalEntradas: 0,
          totalSalidas: 0,
          totalAjustes: 0,
          entradasPorTipo: {},
          salidasPorTipo: {},
          ajustesPorTipo: {},
        });
      }
      const g = bucket.get(k);
      g.movimientos.push(mov);
      const c = Number(mov.cantidad || 0);
      const tipoKey = normalizeTipoKey(mov.tipo_huevo || '');
      if (SET_ENTRADAS.has(mov.tipo_movimiento)) {
        g.totalEntradas += c;
        g.entradasPorTipo[tipoKey] = (g.entradasPorTipo[tipoKey] || 0) + c;
      } else if (SET_SALIDAS.has(mov.tipo_movimiento)) {
        g.totalSalidas += c;
        g.salidasPorTipo[tipoKey] = (g.salidasPorTipo[tipoKey] || 0) + c;
      } else if (SET_AJUSTES.has(mov.tipo_movimiento)) {
        g.totalAjustes += c;
        g.ajustesPorTipo[tipoKey] = (g.ajustesPorTipo[tipoKey] || 0) + c;
      }
    }
    // Ordenar movimientos por created_at desc (más nuevo arriba) dentro de cada día
    for (const g of bucket.values()) {
      g.movimientos.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
    // Ordenar bloques por fecha desc
    return [...bucket.values()].sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [movimientos]);

  // Estado de expansión (día más reciente abierto por defecto)
  const [expanded, setExpanded] = useState(() => new Set());
  useEffect(() => {
    if (gruposPorFecha.length && expanded.size === 0) {
      setExpanded(new Set([gruposPorFecha[0].fecha]));
    }
  }, [gruposPorFecha, expanded.size]);

  const toggleDia = (fecha) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(fecha)) next.delete(fecha);
      else next.add(fecha);
      return next;
    });
  };
  const expandirTodo = () => setExpanded(new Set(gruposPorFecha.map((g) => g.fecha)));
  const colapsarTodo = () => setExpanded(new Set());

  const fmt = (n) => Number(n || 0).toLocaleString('en-US');

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <ArrowLeftRight className="w-5 h-5" /> Historial de Movimientos
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {gruposPorFecha.length > 0 && (
              <>
                <Button variant="ghost" size="sm" onClick={expandirTodo} data-testid="btn-expandir-todo">
                  Expandir todo
                </Button>
                <Button variant="ghost" size="sm" onClick={colapsarTodo} data-testid="btn-colapsar-todo">
                  Colapsar todo
                </Button>
              </>
            )}
            {hayFiltros && (
              <Button
                variant="outline"
                size="sm"
                onClick={limpiarTodo}
                data-testid="btn-limpiar-filtros-mov"
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              Tipo de movimiento
            </label>
            <Select value={filtroMovimiento} onValueChange={setFiltroMovimiento}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filtro-tipo-movimiento">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Entrada">Entradas</SelectItem>
                <SelectItem value="Salida">Salidas</SelectItem>
                <SelectItem value="Rotura">Roturas</SelectItem>
                <SelectItem value="Ajuste">Ajustes</SelectItem>
                <SelectItem value="Reverso">Reversos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              Tipo de huevo
            </label>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[150px] h-9" data-testid="filtro-tipo-huevo-mov">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(EGG_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              Desde
            </label>
            <Input
              type="date"
              value={filtroFechaDesde}
              onChange={(e) => setFiltroFechaDesde(e.target.value)}
              className="w-[150px] h-9"
              data-testid="filtro-fecha-desde"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              Hasta
            </label>
            <Input
              type="date"
              value={filtroFechaHasta}
              onChange={(e) => setFiltroFechaHasta(e.target.value)}
              className="w-[150px] h-9"
              data-testid="filtro-fecha-hasta"
              min={filtroFechaDesde || undefined}
            />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Sliders className="w-3 h-3" />
          {hayFiltros
            ? 'Filtros activos · se guardan automáticamente para la próxima visita.'
            : 'Mostrando los últimos 7 días. Usá los filtros de fecha para consultar otras fechas.'}
        </p>
      </CardHeader>
      <CardContent>
        {gruposPorFecha.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ArrowLeftRight className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{hayFiltros ? 'Sin movimientos para estos filtros' : 'No hay movimientos en el rango seleccionado'}</p>
            {hayFiltros && (
              <Button variant="link" size="sm" onClick={limpiarTodo} className="mt-2">
                Ver todos
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="lista-dias-movimientos">
            {gruposPorFecha.map((g) => {
              const isOpen = expanded.has(g.fecha);
              const tiposConMovimiento = new Set([
                ...Object.keys(g.entradasPorTipo),
                ...Object.keys(g.salidasPorTipo),
                ...Object.keys(g.ajustesPorTipo),
              ]);
              return (
                <div
                  key={g.fecha}
                  className="border border-border rounded-lg bg-card overflow-hidden transition-colors"
                  data-testid={`bloque-fecha-${g.fecha}`}
                >
                  {/* Header clickable */}
                  <button
                    type="button"
                    onClick={() => toggleDia(g.fecha)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none transition-colors"
                    aria-expanded={isOpen}
                    data-testid={`btn-toggle-${g.fecha}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ChevronDown
                        className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
                      />
                      <div className="text-left">
                        <div className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                          {formatDate(g.fecha)}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {g.movimientos.length} movimiento{g.movimientos.length === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {g.totalEntradas > 0 && (
                        <Badge variant="default" className="tabular-nums font-medium gap-1 whitespace-nowrap">
                          <TrendingUp className="w-3 h-3" />
                          +{fmt(g.totalEntradas)}
                        </Badge>
                      )}
                      {g.totalSalidas > 0 && (
                        <Badge variant="destructive" className="tabular-nums font-medium gap-1 whitespace-nowrap">
                          <TrendingDown className="w-3 h-3" />
                          −{fmt(g.totalSalidas)}
                        </Badge>
                      )}
                      {g.totalAjustes > 0 && (
                        <Badge variant="outline" className="tabular-nums font-medium whitespace-nowrap">
                          ± {fmt(g.totalAjustes)}
                        </Badge>
                      )}
                    </div>
                  </button>

                  {/* Subtotales por tipo (siempre visibles debajo del header) */}
                  {tiposConMovimiento.size > 0 && (
                    <div className="px-4 pb-2 pt-0 flex flex-wrap gap-1.5 border-t border-border/50">
                      {[...tiposConMovimiento].map((tipoKey) => {
                        const label = EGG_LABELS[tipoKey] || tipoKey;
                        const ent = g.entradasPorTipo[tipoKey] || 0;
                        const sal = g.salidasPorTipo[tipoKey] || 0;
                        const aju = g.ajustesPorTipo[tipoKey] || 0;
                        const neto = ent - sal + aju;
                        return (
                          <div
                            key={tipoKey}
                            className="text-[11px] rounded-md border border-border/60 bg-muted/40 px-2 py-1 flex items-center gap-1.5"
                          >
                            <span className="capitalize font-medium">{label}:</span>
                            {ent > 0 && (
                              <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">
                                +{fmt(ent)}
                              </span>
                            )}
                            {sal > 0 && (
                              <span className="text-red-600 dark:text-red-400 tabular-nums">
                                −{fmt(sal)}
                              </span>
                            )}
                            {aju !== 0 && (
                              <span className="text-amber-600 dark:text-amber-400 tabular-nums">
                                {aju > 0 ? '+' : ''}{fmt(aju)}
                              </span>
                            )}
                            <span className="text-muted-foreground/60">·</span>
                            <span
                              className={`tabular-nums font-semibold ${
                                neto > 0
                                  ? 'text-emerald-700 dark:text-emerald-300'
                                  : neto < 0
                                  ? 'text-red-700 dark:text-red-300'
                                  : 'text-muted-foreground'
                              }`}
                            >
                              {neto > 0 ? '+' : ''}{fmt(neto)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Detalle expandible */}
                  {isOpen && (
                    <div className="border-t border-border" data-testid={`detalle-${g.fecha}`}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <tr>
                              <th className="text-left px-4 py-2 font-medium">Hora</th>
                              <th className="text-left px-3 py-2 font-medium">Tipo</th>
                              <th className="text-left px-3 py-2 font-medium">Huevo</th>
                              <th className="text-right px-3 py-2 font-medium">Cantidad</th>
                              <th className="text-left px-3 py-2 font-medium">Referencia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.movimientos.map((mov) => (
                              <tr
                                key={mov.id || mov._id}
                                className="border-t border-border/50 hover:bg-accent/40 transition-colors"
                                data-testid={`row-mov-${mov.id || mov._id}`}
                              >
                                <td className="px-4 py-2 text-muted-foreground text-xs tabular-nums whitespace-nowrap">
                                  {formatLocalTime(mov.created_at)}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge
                                    variant={MOVIMIENTO_COLORS[mov.tipo_movimiento] || 'outline'}
                                    className="text-xs whitespace-nowrap"
                                  >
                                    {MOVIMIENTO_LABELS[mov.tipo_movimiento] || mov.tipo_movimiento}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 capitalize">
                                  {EGG_LABELS[normalizeTipoKey(mov.tipo_huevo)] || mov.tipo_huevo}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">
                                  {fmt(mov.cantidad)}
                                </td>
                                <td className="px-3 py-2 text-muted-foreground text-xs max-w-[300px] truncate">
                                  {mov.referencia}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
